import type { Node, Edge } from '@xyflow/react';

/**
 * Returns the set of node IDs belonging to the main connected component
 * (the largest connected component by node count) of the given graph.
 *
 * Edge direction is ignored — the graph is treated as undirected.
 *
 * If the graph has no edges, every node is its own component of size 1,
 * so the returned set contains all node IDs (all components tie for largest).
 */
export function getMainComponentIds(nodes: Node[], edges: Edge[]): Set<string> {
  const adjacency = new Map<string, Set<string>>();
  nodes.forEach(n => adjacency.set(n.id, new Set<string>()));

  edges.forEach(({ source, target }) => {
    if (!adjacency.has(source)) adjacency.set(source, new Set<string>());
    if (!adjacency.has(target)) adjacency.set(target, new Set<string>());
    adjacency.get(source)!.add(target);
    adjacency.get(target)!.add(source);
  });

  const visited = new Set<string>();
  const components: string[][] = [];

  for (const node of nodes) {
    if (visited.has(node.id)) continue;

    const component: string[] = [];
    const queue: string[] = [node.id];
    visited.add(node.id);

    while (queue.length > 0) {
      const current = queue.shift()!;
      component.push(current);

      const neighbors = adjacency.get(current);
      if (!neighbors) continue;

      for (const neighbor of neighbors) {
        if (!visited.has(neighbor)) {
          visited.add(neighbor);
          queue.push(neighbor);
        }
      }
    }

    components.push(component);
  }

  const maxSize = components.reduce((max, c) => Math.max(max, c.length), 0);
  const largestComponents = components.filter(c => c.length === maxSize);

  return new Set(largestComponents.flat());
}
